import { useMemo, useState } from 'react';

import CardDetails from '@components/CardDetails';
import PaypalRow from '@components/PaypalRow';
import { usePaymentMethods } from '@features/PaymentMethods/PaymentMethodContext';
import { getIsCardNotExpired } from '@monofront/business/utils';
import RadioLine from '@monofront/design-system/components/Form/RadioLine';
import {
  IBankAccount,
  ICard,
  IAssignPrimaryPaymentMethodResult,
  IPaymentTokenAccount,
  PaymentMethod,
  PaymentMethodsType,
} from '@monofront/types';
import { useTranslations } from 'next-intl';
import BankAccountRow from '@components/BankAccountRow';
import Loader from '@monofront/design-system/components/Button/Loader';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { ASSIGN_PRIMARY_PAYMENT_METHOD } from '@monofront/business/queries';
import logger from '@utils/logger';
import { toast } from 'react-toastify';
import { trackEvent } from '@monofront/tracking';

interface ISavedPaymentMethodListProps {
  primaryPaymentMethod?: PaymentMethod;
  onUpdatePaymentMethodFinish: () => void;
}

const SavedPaymentMethodList: React.FC<ISavedPaymentMethodListProps> = ({
  primaryPaymentMethod,
  onUpdatePaymentMethodFinish,
}) => {
  const tAccount = useTranslations('Account');
  const tManageSubs = useTranslations('Account.manageMySubscriptionSection');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { register, handleSubmit, control, getValues, formState } = useForm();

  const selectedPaymentRadioId = useMemo(() => {
    return getValues()['existing-payment-method-id'];
  }, [formState, getValues()]);

  const { paymentMethods } = usePaymentMethods();
  const [assignPrimaryPaymentMethod] =
    useMutation<IAssignPrimaryPaymentMethodResult>(
      ASSIGN_PRIMARY_PAYMENT_METHOD,
    );

  // exclude expired cards & exclude the PM currently defined as the primary payment method
  const savedPaymentMethods = paymentMethods.filter(
    (paymentMethod) =>
      !(
        paymentMethod.paymentType === PaymentMethodsType.Card &&
        !getIsCardNotExpired(paymentMethod)
      ) && primaryPaymentMethod?.id !== paymentMethod.id,
  );

  const currentPrimaryPaymentMethods = paymentMethods.find(
    (paymentMethod) => primaryPaymentMethod?.id === paymentMethod.id,
  );

  if (currentPrimaryPaymentMethods) {
    savedPaymentMethods.push(currentPrimaryPaymentMethods);
  }

  const ExpiredTag = (
    <span
      key="expired-tag"
      className="tw-lf-tag tw-lf-tag-sm tw-lf-tag-decorative-red"
    >
      {tAccount('expired')}
    </span>
  );
  const SubscriptionTag = (
    <span
      key="subscription-tag"
      className="tw-lf-tag tw-lf-tag-sm tw-lf-tag-decorative-yellow"
    >
      {tAccount('subscription')}
    </span>
  );
  const ErrorMessage = (
    <p>
      {tAccount.rich('paymentMethodUpdatedError', {
        strong: (children) => <strong>{children}</strong>,
      })}
    </p>
  );

  const handleOnSubmit = async (formData) => {
    setIsLoading(true);

    try {
      const hasAssignedPrimaryPM = await assignPrimaryPaymentMethod({
        variables: {
          paymentMethodId: formData['existing-payment-method-id'],
          paymentMethodSource: formData['existing-payment-method-source'],
        },
      });

      if (!hasAssignedPrimaryPM) {
        throw new Error('Could not assign primary method');
      }

      trackEvent({
        type: 'LFPaymentMethodUpdated',
        data: {
          payment_method_action: 'assigned as primary',
          update_source: 'account membership page',
        },
      });

      onUpdatePaymentMethodFinish();
    } catch (e) {
      logger.error('Could not assign primary payment method.', {
        pm_id: formData['existing-payment-method-id'],
        pm_source: formData['existing-payment-method-source'],
      });

      toast.error(ErrorMessage);
    }

    setIsLoading(false);
  };

  if (savedPaymentMethods.length === 0) return null;

  return (
    <form
      onSubmit={handleSubmit(handleOnSubmit)}
      className="saved-payment-method-list"
    >
      <h3 className="tw-lf-body-bold-md">
        {tManageSubs('useSavedPaymentMethod')}
      </h3>
      <ul className="tw-mt-4">
        {savedPaymentMethods.map((paymentMethod) => (
          <li key={paymentMethod.id}>
            <RadioLine
              id={paymentMethod.id}
              value={paymentMethod.id}
              name="existing-payment-method-id"
              control={control}
              required
              disabled={
                primaryPaymentMethod?.id === paymentMethod.id ||
                (paymentMethod.paymentType === PaymentMethodsType.Card &&
                  !getIsCardNotExpired(paymentMethod))
              }
              isTextBoldWhenChecked
              isVerticallyCentered
              withLowOpacityWhenDisabled
            >
              {paymentMethod.paymentType === PaymentMethodsType.Card && (
                <CardDetails
                  paymentMethod={paymentMethod as ICard}
                  tags={[
                    ...(!getIsCardNotExpired(paymentMethod)
                      ? [ExpiredTag]
                      : []),
                    ...(primaryPaymentMethod?.id === paymentMethod.id
                      ? [SubscriptionTag]
                      : []),
                  ]}
                  withoutBrands
                />
              )}
              {paymentMethod.paymentType ===
                PaymentMethodsType.PaymentTokenAccount && (
                <div className="paypal tw-flex tw-items-center">
                  <PaypalRow
                    paymentMethod={paymentMethod as IPaymentTokenAccount}
                    tags={
                      primaryPaymentMethod?.id === paymentMethod.id
                        ? [SubscriptionTag]
                        : undefined
                    }
                  />
                </div>
              )}
              {paymentMethod.paymentType === PaymentMethodsType.BankAccount && (
                <BankAccountRow
                  paymentMethod={paymentMethod as IBankAccount}
                  tags={
                    primaryPaymentMethod?.id === paymentMethod.id
                      ? [SubscriptionTag]
                      : undefined
                  }
                />
              )}
            </RadioLine>
            {selectedPaymentRadioId === paymentMethod.id && (
              <input
                {...register('existing-payment-method-source', {
                  required: true,
                })}
                type="hidden"
                name="existing-payment-method-source"
                value={paymentMethod.source}
              />
            )}
          </li>
        ))}
      </ul>
      <div className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-10 tw-border-0 tw-border-t tw-border-solid tw-border-blue-200 tw-bg-white tw-p-4">
        <button
          type="submit"
          className={`tw-lf-button tw-lf-button-primary tw-lf-button-md tw-w-full`}
          disabled={!formState.isDirty}
          data-testid="PaymentFormSubmitButton"
        >
          {isLoading ? <Loader /> : tManageSubs('addNewPaymentMethodCTA')}
        </button>
      </div>
    </form>
  );
};

export default SavedPaymentMethodList;
